import React, { useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import {
  getDirectConversations,
  getDirectMessages,
  getOrCreateDirectConversation,
  getUserPublicProfile,
  sendDirectMessage
} from '../lib/supabase'
import './DirectMessages.css'

function getInitials(name) {
  return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
}

function clip(text, max = 70) {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}…` : text
}

export default function DirectMessages() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const targetUsername = searchParams.get('user')

  const [conversations, setConversations] = useState([])
  const [activeConversationId, setActiveConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [loadingConversations, setLoadingConversations] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const handledTargetRef = useRef('')
  const messagesEndRef = useRef(null)

  const activeConversation = useMemo(
    () => conversations.find(c => c.id === activeConversationId) || null,
    [conversations, activeConversationId]
  )

  const loadConversations = async ({ preserveSelection = true } = {}) => {
    if (!user?.id) return
    setLoadingConversations(true)
    try {
      const items = await getDirectConversations(user.id)
      setConversations(items)
      if (!preserveSelection) {
        setActiveConversationId(items[0]?.id || null)
      } else if (items.length > 0 && !items.some(c => c.id === activeConversationId)) {
        setActiveConversationId(items[0].id)
      }
    } catch (e) {
      console.error('Failed to load conversations:', e)
      setError(e.message || 'Could not load conversations.')
    } finally {
      setLoadingConversations(false)
    }
  }

  useEffect(() => {
    loadConversations({ preserveSelection: false })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    const interval = setInterval(() => {
      loadConversations()
    }, 10000)
    return () => clearInterval(interval)
  }, [user?.id, activeConversationId])

  useEffect(() => {
    if (!user?.id || !targetUsername) return
    if (handledTargetRef.current === `${user.id}:${targetUsername}`) return

    let cancelled = false
    const startConversation = async () => {
      try {
        const targetProfile = await getUserPublicProfile(targetUsername)
        if (!targetProfile) throw new Error('User not found.')
        if (targetProfile.id === user.id) return

        const convo = await getOrCreateDirectConversation(user.id, targetProfile.id)
        if (cancelled) return

        await loadConversations()
        if (cancelled) return
        setActiveConversationId(convo.id)
        handledTargetRef.current = `${user.id}:${targetUsername}`
      } catch (e) {
        if (cancelled) return
        console.error('Failed to open DM conversation:', e)
        setError(e.message || 'Could not open conversation.')
      }
    }

    startConversation()
    return () => { cancelled = true }
  }, [user?.id, targetUsername])

  useEffect(() => {
    if (!activeConversationId) {
      setMessages([])
      return
    }

    let cancelled = false
    const load = async () => {
      setLoadingMessages(true)
      try {
        const data = await getDirectMessages(activeConversationId)
        if (!cancelled) setMessages(data)
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load messages:', e)
          setError(e.message || 'Could not load messages.')
        }
      } finally {
        if (!cancelled) setLoadingMessages(false)
      }
    }

    load()
    const interval = setInterval(load, 4000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [activeConversationId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, activeConversationId])

  const handleSend = async () => {
    if (!activeConversation || sending || !draft.trim()) return
    setSending(true)
    setError('')
    const currentDraft = draft
    setDraft('')

    try {
      const newMessage = await sendDirectMessage(activeConversation.id, user.id, currentDraft)
      setMessages(prev => [...prev, newMessage])
      setConversations(prev => prev.map(c => c.id === activeConversation.id ? ({
        ...c,
        last_message_preview: currentDraft.trim().slice(0, 180),
        last_message_sender_id: user.id,
        last_message_at: newMessage.created_at
      }) : c))
    } catch (e) {
      console.error('Failed to send message:', e)
      setDraft(currentDraft)
      setError(e.message || 'Could not send message.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="dm-page">
      <header className="dm-header">
        <button className="dm-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div className="dm-header-title-wrap">
          <div className="dm-header-title">Direct Messages</div>
          <div className="dm-header-subtitle">Private chats, no incubation</div>
        </div>
      </header>

      <main className="dm-body">
        <div className="dm-layout">
          <section className="dm-sidebar">
            <div className="dm-sidebar-title">Conversations</div>

            {loadingConversations && (
              <div className="dm-sidebar-loading"><span className="spinner" /></div>
            )}

            {!loadingConversations && conversations.length === 0 && (
              <div className="dm-sidebar-empty">
                Start a conversation from a user profile by tapping <strong>Message</strong>.
              </div>
            )}

            {!loadingConversations && conversations.length > 0 && (
              <div className="dm-conversation-list">
                {conversations.map(convo => {
                  const other = convo.other_profile
                  const timeAgo = convo.last_message_at
                    ? formatDistanceToNow(new Date(convo.last_message_at), { addSuffix: true })
                    : ''

                  return (
                    <button
                      key={convo.id}
                      type="button"
                      className={`dm-conversation-item ${activeConversationId === convo.id ? 'active' : ''}`}
                      onClick={() => setActiveConversationId(convo.id)}
                    >
                      <div className="dm-conversation-avatar" style={{ background: other?.avatar_color || '#152550' }}>
                        {other?.avatar_url ? (
                          <img src={other.avatar_url} alt="" className="dm-avatar-image" />
                        ) : (
                          getInitials(other?.display_name || other?.username)
                        )}
                      </div>
                      <div className="dm-conversation-copy">
                        <div className="dm-conversation-name-row">
                          <span className="dm-conversation-name">{other?.display_name || other?.username || 'Unknown'}</span>
                          {timeAgo && <span className="dm-conversation-time mono">{timeAgo}</span>}
                        </div>
                        <div className="dm-conversation-preview">
                          {clip(convo.last_message_preview || 'No messages yet')}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </section>

          <section className="dm-chat-panel">
            {!activeConversation && (
              <div className="dm-chat-empty">
                <div className="dm-chat-empty-glyph">✉</div>
                <div className="dm-chat-empty-title">Select a conversation</div>
                <div className="dm-chat-empty-body">
                  Open an existing thread or start one from someone’s profile.
                </div>
              </div>
            )}

            {activeConversation && (
              <>
                <div className="dm-chat-topbar">
                  <div className="dm-chat-topbar-avatar" style={{ background: activeConversation.other_profile?.avatar_color || '#152550' }}>
                    {activeConversation.other_profile?.avatar_url ? (
                      <img src={activeConversation.other_profile.avatar_url} alt="" className="dm-avatar-image" />
                    ) : (
                      getInitials(activeConversation.other_profile?.display_name || activeConversation.other_profile?.username)
                    )}
                  </div>
                  <div className="dm-chat-topbar-meta">
                    <div className="dm-chat-topbar-name">
                      {activeConversation.other_profile?.display_name || activeConversation.other_profile?.username}
                    </div>
                    {activeConversation.other_profile?.username && (
                      <button
                        type="button"
                        className="dm-chat-topbar-link"
                        onClick={() => navigate(`/user/${activeConversation.other_profile.username}`)}
                      >
                        View profile
                      </button>
                    )}
                  </div>
                </div>

                <div className="dm-chat-messages">
                  {loadingMessages && messages.length === 0 && (
                    <div className="dm-chat-loading"><span className="spinner" /></div>
                  )}

                  {!loadingMessages && messages.length === 0 && (
                    <div className="dm-chat-empty-inline">No messages yet. Say hello.</div>
                  )}

                  {messages.map(msg => {
                    const mine = msg.sender_id === user?.id
                    return (
                      <div key={msg.id} className={`dm-bubble-row ${mine ? 'mine' : 'theirs'}`}>
                        <div className={`dm-bubble ${mine ? 'mine' : 'theirs'}`}>
                          <div className="dm-bubble-content">{msg.content}</div>
                          <div className="dm-bubble-time mono">
                            {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <div className="dm-composer">
                  <textarea
                    className="dm-composer-input"
                    placeholder="Write a message…"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    rows={3}
                    maxLength={5000}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSend()
                      }
                    }}
                  />
                  <div className="dm-composer-footer">
                    <span className="mono dm-composer-count">{draft.length}/5000</span>
                    <button
                      className="btn btn-primary dm-send-btn"
                      onClick={handleSend}
                      disabled={sending || !draft.trim()}
                    >
                      {sending ? <span className="spinner" /> : 'Send'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        {error && <div className="dm-global-error">{error}</div>}
      </main>
    </div>
  )
}
